/**
 * LES POINTS D'ATTENTION DU MOIS — vue Mois du planning (31/10/2026).
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * « Le planning, c'est bien mais pas assez. » — Inès, 30/10/2026. Ce qui
 * manquait à la vue Mois n'était pas une couleur de plus : c'était de dire
 * OÙ regarder. Un trou de couverture n'était qu'un point rouge de neuf pixels
 * dans une grille de trente et une colonnes ; le trouver, c'était balayer le
 * mois des yeux.
 *
 * Ce module liste, pour les postes affichés :
 *
 *  · `trou`        — un poste OUVERT ce jour-là et personne dessus ;
 *  · `tous-absents`— des gens sont postés, mais tous sont en absence validée :
 *                    le poste n'est pas tenu (la grille les montre barrés) ;
 *  · `absent`      — quelqu'un est posté pendant son absence validée, alors que
 *                    d'autres tiennent le poste : le poste est couvert, mais le
 *                    planning se contredit.
 *
 * ⚠️ UN JOUR FERMÉ N'EST JAMAIS UN TROU. « Fermé » est une décision d'ouverture
 * (jour chômé, poste qui n'ouvre pas) ; seul « ouvert et personne » est un
 * manque. Un poste sans ouverture connue n'est pas non plus compté : on ne sait
 * pas, et dire « trou » sur un doute ferait crier au loup.
 *
 * Module PUR : aucun import d'exécution, testé depuis `../backend/tests`.
 */

export interface PosteAttention { code: string; libelle: string }

export interface AffectationAttention {
  date: string;
  posteCode: string;
  orgId: string;
  nom: string;
}

export interface OuvertureAttention { etat: string; manque?: number | null }

export type GenreAttention = 'trou' | 'tous-absents' | 'absent';

export interface PointAttention {
  genre: GenreAttention;
  date: string;
  posteCode: string;
  posteLibelle: string;
  /** Une phrase, prête à afficher. */
  detail: string;
}

export interface ResumeAttention {
  /** Postes ouverts qui ne sont PAS tenus : `trou` + `tous-absents`. */
  nonTenus: number;
  /** Postes tenus, mais où quelqu'un est posté pendant son absence : `absent`. */
  surAbsence: number;
}

export function pointsAttention(entree: {
  jours: string[];
  postes: PosteAttention[];
  /** L'ouverture d'un poste un jour donné, ou `null` si elle n'est pas connue. */
  ouverture: (posteCode: string, date: string) => OuvertureAttention | null;
  affectations: AffectationAttention[];
  /** Le motif d'une absence VALIDÉE de la personne ce jour-là, sinon `null`. */
  absence: (orgId: string, date: string) => string | null;
}): PointAttention[] {
  const parCase = new Map<string, AffectationAttention[]>();
  for (const a of entree.affectations) {
    const cle = `${a.posteCode}|${a.date}`;
    const l = parCase.get(cle);
    if (l) l.push(a); else parCase.set(cle, [a]);
  }
  const sortie: PointAttention[] = [];
  for (const date of entree.jours) {
    for (const p of entree.postes) {
      const cases = parCase.get(`${p.code}|${date}`) || [];
      const base = { date, posteCode: p.code, posteLibelle: p.libelle };
      if (!cases.length) {
        const o = entree.ouverture(p.code, date);
        if (o && o.etat === 'ouvert') {
          sortie.push({
            ...base, genre: 'trou',
            detail: o.manque && o.manque > 1 ? `Ouvert, personne (${o.manque} manquants)` : 'Ouvert, personne',
          });
        }
        continue;
      }
      const absents = cases
        .map((c) => ({ c, motif: entree.absence(c.orgId, date) }))
        .filter((x): x is { c: AffectationAttention; motif: string } => !!x.motif);
      if (!absents.length) continue;
      const texte = absents.map((x) => `${x.c.nom} (${x.motif})`).join(', ');
      if (absents.length === cases.length) {
        sortie.push({ ...base, genre: 'tous-absents', detail: `Non tenu : ${texte}` });
      } else {
        sortie.push({ ...base, genre: 'absent', detail: `Posté(e) pendant une absence : ${texte}` });
      }
    }
  }
  return sortie;
}

export function resumeAttention(points: PointAttention[]): ResumeAttention {
  let nonTenus = 0; let surAbsence = 0;
  for (const p of points) {
    if (p.genre === 'absent') surAbsence += 1;
    else nonTenus += 1;
  }
  return { nonTenus, surAbsence };
}
